import React, { useState } from 'react';
import { Select } from 'antd';
import { getOverlayPortalParent, TagColored, TooltipTrigger, useReadOnly } from '@spark';
import EditableLabels from '@/features/tasks/components/EditableLabels';
import { NONE_SELECTED } from '@/features/datasets/components/DataSelection/RawRecordsStatus/constants';
import { DatasetOrigin, TaskType } from '@/shared/domain/taskTypes';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';
import { LoadingOutlined } from '@ant-design/icons';
import { getLabelConfidenceTooltip } from '@/features/tasks/utils/labelConfidenceTooltip';

/**
 * LabelSelector - A shared component for handling label selection
 * Automatically switches between single-select dropdown and multi-select based on task type and multilabel setting
 *
 * @param {Object} props
 * @param {Array} props.labels - Current labels array
 * @param {Function} props.onSave - Callback function when labels are updated
 * @param {Array} props.allLabelOptions - Available label options
 * @param {string} props.taskType - Task type (classification, NER, etc.)
 * @param {boolean} props.isMultilabel - Whether this is a multi-label task
 * @param {Object} props.record - Record object (for EditableLabels compatibility)
 * @param {Object} props.style - Additional styles
 * @param {string} props.placeholder - Placeholder text for single-select
 * @param {boolean} props.showSparkleInTag - Show the AI sparkle (and confidence tooltip) on labels
 */
const LabelSelector = ({
    labels = [],
    onSave,
    allLabelOptions = [],
    taskType,
    isMultilabel = false,
    record,
    style = {},
    placeholder = "Select label",
    showSparkleInTag = false,
    onDropdownVisibleChange,
}) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const { readOnly } = useReadOnly();
    const cleanLabels = Array.isArray(labels) ? labels.map(label => label.trim() || NONE_SELECTED) : [];
    const isSingleLabelClassification = taskType === TaskType.classification && !isMultilabel;

    if (isSingleLabelClassification) {
        const currentLabel = cleanLabels.length > 0 ? cleanLabels[0] : undefined;
        const isAiLabelingInProgress =
            record?.data_creation_type === DatasetOrigin.specific_ai_labeled &&
            (!currentLabel || currentLabel === NONE_SELECTED);

        const renderTag = (labelName, { withSparkle = false } = {}) => {
            const variant = labelName === NONE_SELECTED ? 'disabled' : getSparkTagVariant(labelName, allLabelOptions);
            return (
                <TagColored
                    variant={variant}
                    label={labelName}
                    endIconSlug={withSparkle && labelName !== NONE_SELECTED ? 'sparkles-2' : undefined}
                />
            );
        };

        const tooltip = getLabelConfidenceTooltip({
            record,
            isAiGenerated: showSparkleInTag,
            labelName: currentLabel,
            allLabelOptions,
        });

        const select = (
            <Select
                key={`${record?._id || 'label'}-${currentLabel}`}
                style={{ width: 170, ...style }}
                value={currentLabel}
                disabled={readOnly}
                onChange={(newLabel) => {
                    onSave([newLabel]);
                }}
                options={allLabelOptions.map(opt => ({
                    label: opt,
                    value: opt,
                }))}
                optionRender={(option) => renderTag(option.value)}
                labelRender={({ value }) => (
                    value
                        ? renderTag(value, { withSparkle: showSparkleInTag })
                        : placeholder
                )}
                placeholder={placeholder}
                showSearch
                filterOption={(input, option) =>
                    (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                }
                getPopupContainer={(trigger) => getOverlayPortalParent(trigger)}
                onDropdownVisibleChange={(open) => {
                    setDropdownOpen(open);
                    onDropdownVisibleChange?.(open);
                }}
            />
        );

        return isAiLabelingInProgress ? <div style={{ fontWeight: 300 }}><LoadingOutlined style={{ marginRight: 4 }} /> Generating...</div> : (
            <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                {tooltip ? (
                    <TooltipTrigger text={tooltip} placement="top" disabled={dropdownOpen}>
                        {select}
                    </TooltipTrigger>
                ) : (
                    select
                )}
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
            <EditableLabels
                style={{ flex: 1, ...style }}
                labels={cleanLabels}
                onSave={onSave}
                record={record}
                allLabelOptions={allLabelOptions}
                showSparkleInTag={showSparkleInTag}
                onDropdownVisibleChange={onDropdownVisibleChange}
            />
        </div>
    );
};

export default LabelSelector;
